import { useState, useEffect } from "react";
import { formatObject, getValueFromExtractor } from "../common/formatting";
import useOptions from "../hooks/useOptions";
import { useTranslation } from "../i18n";

/**
 * @param {object} props
 * @param {boolean} props.rotated
 * @param {boolean} props.isActive
 * @returns {React.Component}
 */
function SortingIcon({ rotated, isActive }) {
  return (
    <svg
      className={`h-2 fill-current ${isActive ? "text-white" : "text-c-gray-400"} ${rotated ? "rotate-180" : null}`}
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 330 330"
    >
      <path
        id="XMLID_224_"
        d="M325.606,229.393l-150.004-150C172.79,76.58,168.974,75,164.996,75c-3.979,0-7.794,1.581-10.607,4.394
	l-149.996,150c-5.858,5.858-5.858,15.355,0,21.213c5.857,5.857,15.355,5.858,21.213,0l139.39-139.393l139.397,139.393
	C307.322,253.536,311.161,255,315,255c3.839,0,7.678-1.464,10.607-4.394C331.464,244.748,331.464,235.251,325.606,229.393z"
      />
    </svg>
  );
}

/**
 * @param {object} props
 * @param {() => void} props.onClick
 * @param {boolean} props.isActive
 * @param {"ascending" | "descending"} props.direction
 * @returns {React.Component}
 */
function SortButtons({ onClick, isActive, direction }) {
  return (
    <button onClick={onClick}>
      <div className="flex flex-col space-y-1">
        <SortingIcon
          rotated={false}
          isActive={isActive && direction === "ascending"}
        />
        <SortingIcon
          rotated={true}
          isActive={isActive && direction === "descending"}
        />
      </div>
    </button>
  );
}

const PlaceholderContextMenu = ({ children }) => <>{children}</>;

/**
 * @template T
 * @param {object} props
 * @param {Array<import("../common/formatting").Extractor>} props.head
 * @param {Array<T>} rows
 * @param {string} [props.className]
 * @param {(row: T) => void} [props.onClick]
 * @param {SortDirection} [props.initialSortDirection]
 * @param {React.ComponentType} props.ContextMenu
 * @returns {React.Component}
 */
export default function Table({
  head,
  rows,
  className,
  onClick,
  initialSortDirection,
  ContextMenu = PlaceholderContextMenu,
}) {
  const { t } = useTranslation();
  const defaultSortDirection =
    initialSortDirection != null ? initialSortDirection : "ascending";
  const [sortedBy, setSortedBy] = useState(head[0].title);
  /** @type {[SortDirection, React.Dispatch<SortDirection>]} */
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  /** @type {[SortDirection, React.Dispatch<SortDirection>]} */
  const [innerRows, setInnerRows] = useState(rows || []);
  const { options } = useOptions(head);

  useEffect(() => {
    setInnerRows(rows || []);
  }, [rows]);

  /**
   * @param {string} title
   * @returns {"ascending" | "descending"}
   */
  const getSortDirection = (title) => {
    if (sortedBy === title) {
      const newSortDirection =
        sortDirection === "ascending" ? "descending" : "ascending";
      setSortDirection(newSortDirection);
      return newSortDirection;
    } else {
      const newSortDirection = "ascending";
      setSortedBy(title);
      setSortDirection(newSortDirection);
      return newSortDirection;
    }
  };

  /**
   * @template T
   * @param {Array<T>} rows
   * @param {import("../common/formatting").Extractor} extractor
   * @param {"ascending" | "descending"} how
   * @returns {Array<T>}
   */
  const sortBy = (rows, extractor, how) => {
    console.debug("Sorting", how, rows, "rows by", extractor);
    return [...(rows || [])].sort((a, b) => {
      let lhs = getValueFromExtractor(a, extractor);
      let rhs = getValueFromExtractor(b, extractor);
      if (lhs < rhs) {
        return how === "ascending" ? -1 : 1;
      }

      return how === "ascending" ? 1 : -1;
    });
  };

  return (
    <div
      className={`p-2 w-full bg-c-gray-600 rounded-lg flex flex-col overflow-auto ${className}`}
    >
      <table className="table-auto w-full text-left flex-grow">
        <thead className="sticky">
          <tr className="border-b border-b-c-gray-500">
            {head.map((extractor, i) => {
              return (
                <th key={i} className="px-1 py-1.5">
                  <div
                    className="flex flex-row cursor-pointer"
                    onClick={() => {
                      const newRows = sortBy(
                        innerRows,
                        extractor,
                        getSortDirection(extractor.title),
                      );
                      setInnerRows(newRows);
                    }}
                  >
                    <p className="mr-2">{extractor.title}</p>
                    <SortButtons
                      onClick={() => {
                        const newRows = sortBy(
                          innerRows,
                          extractor,
                          getSortDirection(extractor.title),
                        );
                        setInnerRows(newRows);
                      }}
                      isActive={extractor.title === sortedBy}
                      direction={sortDirection}
                    />
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="overflow-y-scroll">
          {!innerRows || innerRows.length === 0 ? (
            <tr>
              <td>{t("common.noData")}</td>
            </tr>
          ) : (
            innerRows.map((row, i) => {
              const rowClassName = `border-b border-b-c-gray-500 ${
                onClick ? "hover:bg-c-gray-500 cursor-pointer" : null
              }`;

              return (
                <ContextMenu key={i} row={row}>
                  <tr
                    className={rowClassName}
                    onClick={onClick ? () => onClick(row) : undefined}
                  >
                    {head.map((extractor, j) => (
                      <td key={j} className="px-1 py-1.5">
                        {formatObject(
                          getValueFromExtractor(row, extractor),
                          extractor.type,
                          extractor.mapper,
                          options[extractor.extractor],
                        )}
                      </td>
                    ))}
                  </tr>
                </ContextMenu>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
